import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BellIcon, PlusIcon } from 'lucide-react';
import { buttonClasses } from '../../components/ui/Button';
import { StatusBadge } from '../../components/admin/StatusBadge';
import { useAdminData } from '../../contexts/AdminDataContext';
import { byPlannedDate, formatDate, formatPrice } from '../../utils/format';
const ease = [0.23, 1, 0.32, 1] as const;
export function Overview() {
  const {
    cases,
    customerById,
    notifications,
    markNotificationsRead
  } = useAdminData();
  const kpis = [{
    label: 'Nye henvendelser',
    value: cases.filter(c => c.status === 'Ny henvendelse').length,
    primary: true
  }, {
    label: 'Aktive sager',
    value: cases.filter(c => !['Færdig', 'Faktureret'].includes(c.status)).length,
    primary: false
  }, {
    label: 'Planlagte opgaver',
    value: cases.filter(c => c.status === 'Planlagt').length,
    primary: false
  }, {
    label: 'Afventer tilbud',
    value: cases.filter(c => c.status === 'Tilbud sendt').length,
    primary: false
  }, {
    label: 'Færdige – ikke faktureret',
    value: cases.filter(c => c.status === 'Færdig').length,
    primary: false
  }];
  const latest = [...cases].sort((a, b) => b.createdAt.localeCompare(a.createdAt)).slice(0, 6);
  const upcoming = cases.filter(c => c.plannedDate && !['Færdig', 'Faktureret'].includes(c.status)).sort(byPlannedDate);
  const unread = notifications.filter(item => !item.read);
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-492" data-mp="kun5zo3g6zvl">
      <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-493" data-mp="g2vm1wesrsef">
        <div data-id="element-494" data-mp="g12emewd145d">
          <p className="text-sm text-ink-muted" data-id="element-495" data-mp="g0somrbbewzo">God dag</p>
          <h1 className="mt-1 font-display text-[clamp(1.8rem,5vw,2.5rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-496" data-mp="b2r0vt2y29hv">
            Overblik
          </h1>
        </div>
        <Link to="/admin/sager/ny" className={buttonClasses('primary', 'md')} data-id="element-497" data-mp="13rxzst6b913n">
          <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-498" data-mp="2y0bjr85659e8" />
          Opret sag
        </Link>
      </div>

      {unread.length > 0 && <motion.div initial={{
      opacity: 0,
      y: -8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.26,
      ease
    }} className="mt-6 rounded-card border border-accent/30 bg-accent-soft p-4 md:p-5" data-id="element-499" data-mp="32yd55zq2hksv">
          <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-500" data-mp="2t78tb8voug29">
            <div className="flex items-start gap-3" data-id="element-501" data-mp="2f8ivfr11occu">
              <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-accent-deep" data-id="element-502" data-mp="2jbnsvf69pnum">
                <BellIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-503" data-mp="25d7ed225yt1x" />
              </span>
              <div data-id="element-504" data-mp="2aazgeowivvju">
                <ul data-id="element-505" data-mp="2ack908za14f2">
                  {unread.slice(0, 3).map(item => <li key={item.id} className="text-[15px] font-medium text-ink" data-id="element-506" data-mp="3l2nltwei1ss">
                      Ny henvendelse fra {item.customerName}
                    </li>)}
                </ul>
                {unread.length > 3 && <p className="mt-1 text-sm text-ink-soft" data-id="element-507" data-mp="1o2ty6we4yft4">+ {unread.length - 3} flere</p>}
              </div>
            </div>
            <div className="flex gap-2" data-id="element-508" data-mp="22sj4f6135a09">
              <Link to={`/admin/sager/${unread[0].caseId}`} className={buttonClasses('primary', 'sm')} data-id="element-509" data-mp="1y0rm1is32al7">
                Åbn henvendelse
              </Link>
              <button type="button" onClick={markNotificationsRead} className={buttonClasses('secondary', 'sm')} data-id="element-510" data-mp="26t1vgxv5mc1e">
                Markér som læst
              </button>
            </div>
          </div>
        </motion.div>}

      <ul className="mt-7 grid gap-4 sm:grid-cols-2 xl:grid-cols-5" data-id="element-511" data-mp="rbrq65mlk6j0">
        {kpis.map((kpi, index) => <motion.li key={kpi.label} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.26,
        ease,
        delay: index * 0.04
      }} className={`rounded-card border p-5 ${kpi.primary ? 'border-accent/30 bg-accent-soft' : 'border-line bg-white'}`} data-id="element-512" data-mp="mdhvdw4sb7xx" data-mpi={index}>
            <p className="font-display text-[2.5rem] font-bold leading-none tracking-tight text-ink" data-id="element-513" data-mp="3jhr23uxnu0x9">
              {kpi.value}
            </p>
            <p className="mt-2 text-[15px] text-ink-soft" data-id="element-514" data-mp="2qvz7r3qcczyn">{kpi.label}</p>
          </motion.li>)}
      </ul>

      <section className="mt-8 rounded-card border border-line bg-white" data-id="element-515" data-mp="2vusnl2t3p31z">
        <header className="flex items-center justify-between gap-3 border-b border-line px-5 py-4" data-id="element-516" data-mp="2zvchlxidnqjw">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-517" data-mp="34mkreunmddxl">Seneste sager</h2>
          <Link to="/admin/sager" className="text-sm font-medium text-accent hover:text-accent-deep" data-id="element-518" data-mp="28580c7pwzjmw">
            Se alle
          </Link>
        </header>

        <div className="hidden md:block" data-id="element-519" data-mp="2c3nju1mse9vk">
          <table className="w-full text-left text-[15px]" data-id="element-520" data-mp="24mr3bglku5yc">
            <thead data-id="element-521" data-mp="24k4vdfurxofl">
              <tr className="border-b border-line text-xs uppercase tracking-wider text-ink-muted" data-id="element-522" data-mp="1znnynx9ie1qw">
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-523" data-mp="1vlb321oh7k7u">Kunde</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-524" data-mp="1vnnfjzbgt2di">Opgave</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-525" data-mp="1qmgcueo6itdp">Dato</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-526" data-mp="1loeszrixiq16">Status</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-527" data-mp="1gwod76183a2d">Pris</th>
                <th scope="col" className="px-5 py-3" data-id="element-528" data-mp="1bxtmlhx0kh7p" />
              </tr>
            </thead>
            <tbody className="divide-y divide-line" data-id="element-529" data-mp="17w8c237ycf0o">
              {latest.map(item => <tr key={item.id} className="transition-colors duration-200 ease-smooth hover:bg-canvas" data-id="element-530" data-mp="17uf4mlpilqrm">
                  <td className="px-5 py-3.5 text-ink" data-id="element-531" data-mp="xy2axntyzcwv">{customerById(item.customerId)?.name}</td>
                  <td className="px-5 py-3.5 text-ink-soft" data-id="element-532" data-mp="oag8aj5jnznd">
                    <span className="text-ink-faint" data-id="element-533" data-mp="o8veivr20kqk">#{item.number}</span> {item.title}
                  </td>
                  <td className="whitespace-nowrap px-5 py-3.5 text-ink-soft" data-id="element-534" data-mp="taujga2fzppm">{formatDate(item.plannedDate)}</td>
                  <td className="px-5 py-3.5" data-id="element-535" data-mp="eed477r4tqxs"><StatusBadge status={item.status} data-id="element-536" data-mp="jb3jvtz0ykio" /></td>
                  <td className="whitespace-nowrap px-5 py-3.5 font-medium text-ink" data-id="element-537" data-mp="jblqnkjbch4u">
                    {formatPrice(item.estimate || item.fixedPrice)}
                  </td>
                  <td className="px-5 py-3.5 text-right" data-id="element-538" data-mp="1bypq79zn0398">
                    <Link to={`/admin/sager/${item.id}`} className={buttonClasses('secondary', 'sm')} data-id="element-539" data-mp="xbecp2jwa96h">
                      Åbn
                    </Link>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>

        <ul className="divide-y divide-line md:hidden" data-id="element-540" data-mp="1pyh6jhdmlvqe">
          {latest.map(item => <li key={item.id} data-id="element-541" data-mp="1urtl0yolcm5f">
              <Link to={`/admin/sager/${item.id}`} className="block px-5 py-4" data-id="element-542" data-mp="3fgxnz8zwc6j5">
                <div className="flex items-start justify-between gap-3" data-id="element-543" data-mp="3feurq785hh5z">
                  <div className="min-w-0" data-id="element-544" data-mp="3ahbb50nzcsav">
                    <p className="truncate text-[15px] font-medium text-ink" data-id="element-545" data-mp="3p6qwg14qs5j6">{item.title}</p>
                    <p className="mt-0.5 text-sm text-ink-muted" data-id="element-546" data-mp="3k7nsyl4eu7db">
                      #{item.number} · {customerById(item.customerId)?.name}
                    </p>
                  </div>
                  <StatusBadge status={item.status} data-id="element-547" data-mp="2wjwg9geyjsbi" />
                </div>
                <p className="mt-2 text-sm text-ink-soft" data-id="element-548" data-mp="2rktb7vyz9m3j">
                  {formatDate(item.plannedDate)} · {formatPrice(item.estimate || item.fixedPrice)}
                </p>
              </Link>
            </li>)}
        </ul>
      </section>

      <section className="mt-6 rounded-card border border-line bg-white" data-id="element-549" data-mp="krziod3jdo8u">
        <header className="border-b border-line px-5 py-4" data-id="element-550" data-mp="gqxjfw9mdc6v">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-551" data-mp="gqdzka0esdq7">Kommende opgaver</h2>
        </header>
        <ul className="divide-y divide-line" data-id="element-552" data-mp="2a7jpbfsv1fvq">
          {upcoming.map(item => <li key={item.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4" data-id="element-553" data-mp="2f99518r12hq5">
              <div className="min-w-0" data-id="element-554" data-mp="2k6iyr0lg9fjs">
                <p className="truncate text-[15px] font-medium text-ink" data-id="element-555" data-mp="2oyaiar66ul0f">{item.title}</p>
                <p className="mt-0.5 truncate text-sm text-ink-muted" data-id="element-556" data-mp="2t05rehud22or">
                  {customerById(item.customerId)?.name} · {item.address}
                </p>
              </div>
              <div className="flex items-center gap-3" data-id="element-557" data-mp="2y1bjcdg75lx8">
                <span className="whitespace-nowrap text-sm text-ink-soft" data-id="element-558" data-mp="32zd6b9gqu5dz">{formatDate(item.plannedDate)}</span>
                <StatusBadge status={item.status} data-id="element-559" data-mp="37w3hbidoyasj" />
                <Link to={`/admin/sager/${item.id}`} className={buttonClasses('secondary', 'sm')} data-id="element-560" data-mp="18kk9x881dakz">
                  Åbn
                </Link>
              </div>
            </li>)}
          {upcoming.length === 0 && <li className="px-5 py-8 text-center text-sm text-ink-muted" data-id="element-561" data-mp="3quov1f4q23ld">Ingen planlagte opgaver endnu.</li>}
        </ul>
      </section>
    </div>;
}